"use client";

import { useRouter } from "next/navigation";
import { useCallback, useEffect, useSyncExternalStore } from "react";

import { api, MallApiError, registerSessionDropped } from "./api";
import type { ManagedSeller, MallUser } from "./types";

/*
 * 로그인 상태.
 *
 * 🔑 **Context 를 두지 않고 모듈 저장소로 둡니다.** 로그인 상태는 앱 전체에서
 *    하나뿐이고, Provider 를 `layout.tsx` 에 끼우면 준비 중 화면(`COMING_SOON`)
 *    까지 그 아래로 들어갑니다. 여기 두면 실제로 쓰는 화면만 구독합니다.
 *
 * 🔴 **토큰은 이 파일에 없습니다.** 브라우저는 토큰을 만질 수 없습니다 —
 *    httpOnly 쿠키(`tnf_member`)라 BFF 만 읽습니다 (오너 결정 #2). 여기 있는 것은
 *    "지금 누구로 보이는가" 뿐이고, 진짜 판정은 언제나 서버가 합니다.
 */

export type SessionState =
  | { status: "loading"; user: null }
  | { status: "guest"; user: null }
  | { status: "member"; user: MallUser };

const LOADING: SessionState = { status: "loading", user: null };
const GUEST: SessionState = { status: "guest", user: null };

let state: SessionState = LOADING;
let inflight: Promise<void> | null = null;

const listeners = new Set<() => void>();

function publish(next: SessionState): void {
  state = next;
  for (const listener of listeners) listener();
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

function snapshot(): SessionState {
  return state;
}

/*
 * 🔴 서버 렌더에서는 늘 `loading` 입니다. 여기서 `guest` 로 추측하면 hydration 이
 *    어긋나 화면이 한 번 깜빡입니다 — 로그인한 사람에게 "로그인" 버튼이 스쳤다가
 *    바뀌는 그 현상입니다.
 */
function serverSnapshot(): SessionState {
  return LOADING;
}

/** 서버에 다시 물어 상태를 맞춥니다. 중복 호출은 한 번으로 합칩니다. */
export function loadSession(force = false): Promise<void> {
  if (!force && state.status !== "loading") return Promise.resolve();
  if (inflight) return inflight;

  inflight = (async () => {
    try {
      /*
       * 🔴 `keepOnUnauthorized` — 로그인하지 않은 사람에게 `/me` 의 401 은
       *    정상입니다. 이걸 빼면 홈에 들어온 손님이 곧바로 로그인 화면으로
       *    끌려갑니다.
       */
      const user = await api.get<MallUser>("/me", { keepOnUnauthorized: true });
      publish(user ? { status: "member", user } : GUEST);
    } catch (error) {
      if (error instanceof MallApiError && error.httpStatus === 401) {
        publish(GUEST);
        return;
      }
      /*
       * 🔑 네트워크 오류를 "로그아웃" 으로 읽지 않습니다. 지하철에서 잠깐 끊긴
       *    것으로 세션을 버리면, 돌아왔을 때 다시 로그인해야 합니다.
       *    상태를 그대로 두고 다음 호출에서 다시 시도합니다.
       */
      if (state.status === "loading") publish(GUEST);
    } finally {
      inflight = null;
    }
  })();

  return inflight;
}

/** 로그인·가입·내 정보 수정이 성공했을 때. */
export function setSessionUser(user: MallUser): void {
  publish({ status: "member", user });
}

/** 로그아웃했을 때, 그리고 서버가 401 을 돌려줬을 때. */
export function clearSession(): void {
  publish(GUEST);
}

/* 🔑 `_lib/api.ts` 는 이 파일을 import 하지 않습니다 — 서로를 부르면 순환입니다.
   대신 401 을 만났을 때 부를 함수를 여기서 등록해 둡니다. */
registerSessionDropped(clearSession);

export function useSession(): SessionState & { reload: () => Promise<void> } {
  const current = useSyncExternalStore(subscribe, snapshot, serverSnapshot);

  useEffect(() => {
    void loadSession();
  }, []);

  const reload = useCallback(() => loadSession(true), []);

  return { ...current, reload } as SessionState & { reload: () => Promise<void> };
}

/**
 * 로그인이 필요한 화면에서 씁니다 — 손님이면 로그인 화면으로 보냅니다.
 *
 * 🔴 `router.replace` 입니다. `push` 를 쓰면 로그인 뒤 뒤로 가기를 눌렀을 때
 *    다시 이 화면 → 다시 로그인 화면으로 튕겨 나갑니다.
 */
export function useRequireMember(): SessionState {
  const session = useSession();
  const router = useRouter();

  useEffect(() => {
    if (session.status !== "guest" || typeof window === "undefined") return;
    const here = `${window.location.pathname}${window.location.search}`;
    router.replace(`/login?next=${encodeURIComponent(here)}`);
  }, [session.status, router]);

  return session;
}

/** 이 사람이 관리하는 판매처 중 `id` 인 것. 없으면 그 화면에 들어갈 수 없습니다. */
export function findManagedSeller(user: MallUser | null, sellerId: number): ManagedSeller | null {
  return user?.managedSellers?.find((seller) => seller.id === sellerId) ?? null;
}
